import React, { useState, useEffect } from 'react';
import styles from './EditBankModal.module.css';
import Select from "react-select";

// Up-to-date 2026 Nigerian NUBAN-compliant banks
const NIGERIAN_BANKS = [
  "Access Bank Plc",
  "Citibank Nigeria Ltd",
  "Ecobank Nigeria Ltd",
  "Fidelity Bank Plc",
  "First Bank of Nigeria Ltd",
  "First City Monument Bank (FCMB)",
  "Globus Bank Ltd",
  "Guaranty Trust Bank (GTBank)",
  "Jaiz Bank Plc",
  "Keystone Bank Ltd",
  "Lotus Bank Ltd",
  "Moniepoint Microfinance Bank Ltd",
  "Optimus Bank",
  "Opay",
  "Parallex Bank Ltd",
  "Palmpay",
  "Polaris Bank Ltd",
  "Premium Trust Bank",
  "Providus Bank Plc",
  "Signature Bank Ltd",
  "Stanbic IBTC Bank Plc",
  "Standard Chartered Bank Ltd",
  "Sterling Bank Plc",
  "SunTrust Bank Ltd",
  "TAJBank",
  "Titan Trust Bank Ltd",
  "Union Bank of Nigeria Plc",
  "United Bank for Africa (UBA)",
  "Unity Bank Plc",
  "Wema Bank Plc",
  "Zenith Bank Plc"
];

const ACCOUNT_TYPES = [
  "Savings Account",
  "Current Account",
  "Corporate / Business Account",
  "Domiciliary Account",
  "Salary Account",
  "Student Account",
  "Fixed Deposit Account"
];

const GLOBAL_CURRENCIES = [
  { code: "NGN", name: "Nigerian Naira (₦)" },
  { code: "USD", name: "United States Dollar ($)" },
  { code: "GBP", name: "British Pound Sterling (£)" },
  { code: "EUR", name: "Euro (€)" },
  { code: "CAD", name: "Canadian Dollar (C$)" },
  { code: "AUD", name: "Australian Dollar (A$)" },
  { code: "ZAR", name: "South African Rand (R)" },
  { code: "GHS", name: "Ghanaian Cedi (₵)" },
  { code: "AED", name: "UAE Dirham (AED)" },
  { code: "CNY", name: "Chinese Yuan (¥)" },
  { code: "JPY", name: "Japanese Yen (¥)" }
];

// 1. Swap initialData prop to memberData to tie into primary view context
const EditBankModal = ({ isOpen, onClose, memberData, onSave }) => {
  const [formData, setFormData] = useState({
    bankName: '',
    accountName: '',
    accountNumber: '',
    accountType: '',
    branch: '',
    currency: 'NGN'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track if "Not on the list" option is chosen
  const [showCustomBank, setShowCustomBank] = useState(false);

  // 2. Hydrate local states on change or open
  useEffect(() => {
    if (isOpen && memberData) {
      const currentBank = memberData.bank_name || '';
      
      // If bank name exists but isn't in our predefined NUBAN array, treat it as a custom bank entry
      const isCustomBank = currentBank !== '' && !NIGERIAN_BANKS.includes(currentBank);
      setShowCustomBank(isCustomBank);

      setFormData({
        bankName: currentBank,
        accountName: memberData.bank_account_name || '',
        accountNumber: memberData.bank_account_number || '',
        accountType: memberData.account_type || '',
        branch: memberData.bank_branch || '',
        currency: memberData.currency || 'NGN'
      });
    }
  }, [isOpen, memberData]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "bankNameSelect") {
      if (value === "custom") {
        setShowCustomBank(true);
        setFormData(prev => ({ ...prev, bankName: '' })); // clear to let them type
      } else {
        setShowCustomBank(false);
        setFormData(prev => ({ ...prev, bankName: value }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleResetCustomBank = () => {
    setShowCustomBank(false);
    setFormData(prev => ({ ...prev, bankName: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const payload = {
        bank_name: formData.bankName,
        bank_account_name: formData.accountName,
        bank_account_number: formData.accountNumber,
        account_type: formData.accountType,
        bank_branch: formData.branch,
        currency: formData.currency,
        bvn: memberData?.bvn || '',
      };

      const saved = await onSave(payload);
      if (saved !== false) {
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const bankOptions = [
    {
      value: "",
      label: "Select Your Bank",
    },
    ...NIGERIAN_BANKS.map((bank) => ({
      value: bank,
      label: bank,
    })),
    {
      value: "custom",
      label: "Other / Not on the list ➔",
    },
  ];

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>Edit Bank Details</h2>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close modal">
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formColumn}>
            
            {/* Bank Name Field (Select Dropdown OR Custom Text Input) */}
            <div className={styles.formGroup}>
              <label>Bank Name <span className={styles.required}>*</span></label>
              {!showCustomBank ? (
                <Select
                  options={bankOptions}
                  value={
                    formData.bankName
                      ? bankOptions.find(option => option.value === formData.bankName) || null
                      : null
                  }
                  onChange={(selected) => {
                    if (selected?.value === "custom") {
                      setShowCustomBank(true);
                      setFormData((prev) => ({
                        ...prev,
                        bankName: "",
                      }));
                    } else {
                      setShowCustomBank(false);
                      setFormData((prev) => ({
                        ...prev,
                        bankName: selected?.value || "",
                      }));
                    }
                  }}
                  placeholder="Select Bank"
                  isSearchable
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "45px",
                      borderRadius: "8px",
                      borderColor: "#d1d5db",
                      boxShadow: "none",
                      "&:hover": {
                        borderColor: "#16A34A",
                      },
                    }),
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 9999,
                    }),
                  }}
                />
              ) : (
                <div className={styles.inputInputWrapper}>
                  <input
                    type="text"
                    placeholder="Enter Bank Name"
                    value={formData.bankName}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        bankName: e.target.value,
                      }))
                    }
                    required
                  />
                  <button 
                    type="button" 
                    className={styles.resetBankBtn}
                    onClick={handleResetCustomBank}
                  >
                    Back to list
                  </button>
                </div>
              )}
            </div>

            {/* Account Name */}
            <div className={styles.formGroup}>
              <label htmlFor="accountName">Account Name <span className={styles.required}>*</span></label>
              <input 
                type="text" 
                id="accountName"
                name="accountName" 
                value={formData.accountName} 
                onChange={handleChange} 
                required 
              />
            </div>

            {/* Account Number */}
            <div className={styles.formGroup}>
              <label htmlFor="accountNumber">Account Number <span className={styles.required}>*</span></label>
              <input 
                type="text" 
                id="accountNumber"
                name="accountNumber" 
                maxLength={10}
                placeholder="10-digit NUBAN number"
                value={formData.accountNumber} 
                onChange={handleChange} 
                required 
              />
            </div>

            {/* Account Type */}
            <div className={styles.formGroup}>
              <label htmlFor="accountType">Account Type </label>
              <select 
                id="accountType"
                name="accountType" 
                value={formData.accountType} 
                onChange={handleChange} 
                required
              >
                <option value="">Select Account Type</option>
                {ACCOUNT_TYPES.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {/* Branch */}
            <div className={styles.formGroup}>
              <label htmlFor="branch">Branch </label>
              <input 
                type="text" 
                id="branch"
                name="branch" 
                placeholder="e.g. Ikeja, Allen Avenue"
                value={formData.branch} 
                onChange={handleChange} 
                required 
              />
            </div>

            {/* Currency */}
            <div className={styles.formGroup}>
              <label htmlFor="currency">Currency </label>
              <select 
                id="currency"
                name="currency" 
                value={formData.currency} 
                onChange={handleChange} 
                required
              >
                {GLOBAL_CURRENCIES.map((curr) => (
                  <option key={curr.code} value={curr.code}>
                    {curr.code} - {curr.name}
                  </option>
                ))}
              </select>
            </div>

          </div>

          <div className={styles.formActions}>
            <button type="button" className={styles.cancelBtn} onClick={onClose} disabled={isSubmitting}>Cancel</button>
            <button type="submit" className={styles.saveBtn} disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditBankModal;